import { useEffect, useRef } from 'react';

/**
 * useKeyboardMovement
 * Tracks pressed keys for WASD + Arrow keys and exposes a ref of the current pressed state.
 * Disabled on mobile and when the user is typing into an input/textarea.
 *
 * @param {boolean} disabled - if true, no listeners are attached.
 * @param {function} onKeyActivity - optional callback fired once on first key press (used to cancel click-to-move targets).
 * @returns {{ keysPressedRef: React.MutableRefObject<{up:boolean,down:boolean,left:boolean,right:boolean}> }}
 */
export default function useKeyboardMovement(disabled = false, onKeyActivity = null) {
  const keysPressedRef = useRef({ up: false, down: false, left: false, right: false });

  useEffect(() => {
    if (disabled) return undefined;

    const isTypingTarget = (target) => {
      if (!target) return false;
      const tag = (target.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
    };

    const setKey = (key, down) => {
      if (key === 'w' || key === 'W' || key === 'ArrowUp') keysPressedRef.current.up = down;
      else if (key === 's' || key === 'S' || key === 'ArrowDown') keysPressedRef.current.down = down;
      else if (key === 'a' || key === 'A' || key === 'ArrowLeft') keysPressedRef.current.left = down;
      else if (key === 'd' || key === 'D' || key === 'ArrowRight') keysPressedRef.current.right = down;
      else return false;
      return true;
    };

    const handleKeyDown = (e) => {
      if (isTypingTarget(e.target)) return;
      if (setKey(e.key, true)) {
        if (onKeyActivity) onKeyActivity();
        e.preventDefault();
      }
    };
    const handleKeyUp = (e) => {
      setKey(e.key, false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      keysPressedRef.current = { up: false, down: false, left: false, right: false };
    };
  }, [disabled, onKeyActivity]);

  return { keysPressedRef };
}
